import { useDocumentTitle } from "@uidotdev/usehooks";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import {
  ACCOUNT_CREATION_FLOW,
  ACCOUNT_CREATION_ROUTE,
  ACCOUNT_SETUP_ROUTE,
  BASE_ROUTE,
  CONFIRM_SITE_ACCESS_ROUTE,
  EMPTY,
  ENABLE_ACCOUNT_ROUTE,
  FORGOT_PASSWORD_ALLOW,
  FORGOT_PASSWORD_ROUTE,
  FORGOT_USERNAME_EMAIL_SENT,
  FORGOT_USERNAME_ROUTE,
  LEGAL_NOTICE_ROUTE,
  LINK_APPLICATION_RESULTS_ROUTE,
  LINK_APPLICATION_ROUTE,
  LOGIN_INFORMATION_ROUTE,
  LOGIN_ROUTE,
  LOGIN_VERIFICATION_METHOD_ROUTE,
  MANAGE_VERIFICATION_METHOD,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  NAME_AND_CONTACT_ROUTE,
  PROFILE_BASE_ROUTE,
  PROVIDER_ACCOUNT_CREATION,
  PROVIDER_INFO,
  PROVIDER_REVIEW_INFO,
  RESET_PASSWORD_ROUTE,
  REVIEW_APPLICATION_ROUTE,
  SEARCH_HAP_ID_ROUTE,
  SECURITY_PREFERENCES_ROUTE,
  SESSION_AUTH_TOKEN,
  SITE_ACCESS_ROUTE,
  VERIFY_ACCOUNT_ROUTE,
} from "../../Constants/CommonConstants";
import { manageAccountCreationSelector } from "../../Selectors/AccountCreationSelector";
import { manageAuthorizationSelector } from "../../Selectors/AuthorizationSelector";
import { manageLinkApplicationSelector } from "../../Selectors/LinkApplicationSelector";
import { manageLoginSelector } from "../../Selectors/LoginSelector";
import { manageProviderAccountCreationSelector } from "../../Selectors/ProviderAccountCreationSelector";
import { getDocumentTitleByRole } from "../../Services/CommonServices";
import AccountSetup from "../AccountCreation/AccountSetup/AccountSetup";
import ChooseAccountType from "../AccountCreation/ChooseAccountType/ChooseAccountType";
import NameAndContactInfo from "../AccountCreation/NameAndContact/NameAndContactInfo";
import ReviewYourInformation from "../AccountCreation/ReviewYourInformation/ReviewYourInformation";
import AuthorizationWrapper from "../AuthorizationWrapper";
import LinkApplicationResults from "../LinkApplication/LinkApplicationResults/LinkApplicationResults";
import LinkMyApplication from "../LinkApplication/LinkMyApplication/LinkMyApplication";
import SearchHAPId from "../LinkApplication/SearchForYourHAPId/SearchHAPId";
import AddEditVerificationMethod from "../Login/AddEditVerificationMethod";
import EnableYourAccount from "../Login/EnableYourAccount/EnableYourAccount";
import ForgotPassword from "../Login/ForgotPassword/ForgotPassword";
import ResetPassword from "../Login/ForgotPassword/ResetPassword";
import ForgotUserName from "../Login/ForgotUsername/ForgotUsername";
import UsernameSent from "../Login/ForgotUsername/UsernameSent";
import LegalNotice from "../Login/LegalNotice/LegalNotice";
import Login from "../Login/Login";
import LoginVerificationMethods from "../Login/LoginVerificationMethods/LoginVerificationMethods";
import VerifyAccount from "../Login/VerifyAccount";
import LoginInformation from "../MyProfile/LoginInformation/LoginInformation";
import PersonalDetails from "../MyProfile/PersonalDetails/PersonalDetails";
import ConfirmSiteAccess from "../ProviderAndCertifierAccountCreation/ConfirmSiteAccess/ConfirmSiteAccess";
import AccountCreationWrapper from "../ProviderAndCertifierAccountCreation/ProviderAccountCreation/AccountCreationWrapper";
import ProviderReviewInfoWrapper from "../ProviderAndCertifierAccountCreation/ProviderReviewInformation/ProviderReviewInfoWrapper";
import RoutesLayout from "./RoutesLayout";
import SiteAccess from "../MyProfile/SiteAccess/SiteAccess";

const jwtTokenFromSession = sessionStorage.getItem(SESSION_AUTH_TOKEN);

/**
 * @description AppRoutes component which contains all the application route definitions.
 * @returns {ReactNode} Render the routes that are required for the application screen navigation.
 */
const AppRoutes = () => {
  const location = useLocation();

  const isProfileRoute = location.pathname?.includes(PROFILE_BASE_ROUTE);

  const { scopes, jwtToken } = useSelector((state) =>
    manageAuthorizationSelector(state)
  );

  useDocumentTitle(getDocumentTitleByRole(scopes, isProfileRoute));

  const { responseDataFirstName } = useSelector((state) =>
    manageLinkApplicationSelector(state)
  );

  const { firstName, userName } = useSelector((state) =>
    manageAccountCreationSelector(state)
  );

  const {
    isUsernameRetrieved,
    flw,
    loginToken,
    isPasswordExpired,
    isPasswordChangeFlag,
    isAddEditVerificationFlow,
  } = useSelector((state) => manageLoginSelector(state));

  const { username, userInvitationId } = useSelector((state) =>
    manageProviderAccountCreationSelector(state)
  );

  const determineRouteForVerifyAccount = useMemo(() => {
    if (
      sessionStorage.getItem(ACCOUNT_CREATION_FLOW) ===
      PROVIDER_ACCOUNT_CREATION
    ) {
      if (isEmpty(username)) {
        return (
          <Navigate
            to={`/${ACCOUNT_CREATION_ROUTE}/${CONFIRM_SITE_ACCESS_ROUTE}`}
          />
        );
      }
      return <VerifyAccount />;
    }
    if (isEmpty(firstName) || isEmpty(userName)) {
      return <Navigate to={`/${ACCOUNT_CREATION_ROUTE}`} />;
    }
    return <VerifyAccount />;
  }, [userName, firstName, username]);

  return (
    <Routes>
      <Route path={BASE_ROUTE} element={<RoutesLayout />}>
        <Route path={EMPTY} element={<Navigate to={LOGIN_ROUTE} />} />
        {/**Account Creation -Routing **/}
        <Route path={ACCOUNT_CREATION_ROUTE} element={<Outlet />}>
          <Route path={EMPTY} element={<ChooseAccountType />} />
          <Route
            path={NAME_AND_CONTACT_ROUTE}
            element={<NameAndContactInfo />}
          />
          <Route
            path={ACCOUNT_SETUP_ROUTE}
            element={
              isEmpty(firstName) ? (
                <Navigate to={`/${ACCOUNT_CREATION_ROUTE}`} />
              ) : (
                <AccountSetup />
              )
            }
          />
          <Route
            path={REVIEW_APPLICATION_ROUTE}
            element={
              isEmpty(userName) || isEmpty(firstName) ? (
                <Navigate to={`/${ACCOUNT_CREATION_ROUTE}`} />
              ) : (
                <ReviewYourInformation />
              )
            }
          />
          <Route
            path={VERIFY_ACCOUNT_ROUTE}
            element={determineRouteForVerifyAccount}
          />
          <Route
            path={CONFIRM_SITE_ACCESS_ROUTE}
            element={<ConfirmSiteAccess />}
          />
          <Route
            path={PROVIDER_INFO}
            element={
              userInvitationId ? (
                <AccountCreationWrapper />
              ) : (
                <Navigate
                  to={`/${ACCOUNT_CREATION_ROUTE}/${CONFIRM_SITE_ACCESS_ROUTE}`}
                />
              )
            }
          />
          <Route
            path={PROVIDER_REVIEW_INFO}
            element={
              isEmpty(username) ? (
                <Navigate
                  to={`/${ACCOUNT_CREATION_ROUTE}/${CONFIRM_SITE_ACCESS_ROUTE}`}
                />
              ) : (
                <ProviderReviewInfoWrapper />
              )
            }
          />
        </Route>
        {/**Link Application -Routing **/}
        <Route path={LINK_APPLICATION_ROUTE} element={<Outlet />}>
          <Route path={EMPTY} element={<LinkMyApplication />} />
          <Route path={SEARCH_HAP_ID_ROUTE} element={<SearchHAPId />} />
          <Route
            path={LINK_APPLICATION_RESULTS_ROUTE}
            element={
              isEmpty(responseDataFirstName) ? (
                <Navigate to={`/${LINK_APPLICATION_ROUTE}`} />
              ) : (
                <LinkApplicationResults />
              )
            }
          />
        </Route>
        {/**Login -Routing **/}
        <Route path={LOGIN_ROUTE} element={<Outlet />}>
          <Route path={EMPTY} element={<Login />} />
          <Route
            path={VERIFY_ACCOUNT_ROUTE}
            element={
              isEmpty(loginToken) ? (
                <Navigate to={`/${LOGIN_ROUTE}`} />
              ) : (
                <VerifyAccount />
              )
            }
          />
          <Route
            path={LEGAL_NOTICE_ROUTE}
            element={
              isEmpty(jwtToken) ? (
                <Navigate to={`/${LOGIN_ROUTE}`} />
              ) : (
                <LegalNotice />
              )
            }
          />
          <Route path={ENABLE_ACCOUNT_ROUTE} element={<EnableYourAccount />} />
          <Route
            path={LOGIN_VERIFICATION_METHOD_ROUTE}
            element={
              isEmpty(loginToken) ? (
                <Navigate to={`/${LOGIN_ROUTE}`} />
              ) : (
                <LoginVerificationMethods />
              )
            }
          />
          <Route
            path={MANAGE_VERIFICATION_METHOD}
            element={
              !isAddEditVerificationFlow ? (
                <Navigate to={`/${LOGIN_ROUTE}`} />
              ) : (
                <AddEditVerificationMethod />
              )
            }
          />
          <Route
            path={RESET_PASSWORD_ROUTE}
            element={
              isPasswordExpired || isPasswordChangeFlag ? (
                <ResetPassword />
              ) : (
                <Navigate to={`/${LOGIN_ROUTE}`} />
              )
            }
          />
        </Route>
        {/**Forgot Password -Routing **/}
        <Route path={FORGOT_PASSWORD_ROUTE} element={<Outlet />}>
          <Route path={EMPTY} element={<ForgotPassword />} />
          <Route
            path={RESET_PASSWORD_ROUTE}
            element={
              flw === FORGOT_PASSWORD_ALLOW ? (
                <ResetPassword />
              ) : (
                <Navigate to={`/${FORGOT_PASSWORD_ROUTE}`} />
              )
            }
          />
          <Route
            path={VERIFY_ACCOUNT_ROUTE}
            element={
              isEmpty(loginToken) ? (
                <Navigate to={`/${FORGOT_PASSWORD_ROUTE}`} />
              ) : (
                <VerifyAccount />
              )
            }
          />
        </Route>
        {/**Forgot Username -Routing **/}
        <Route path={FORGOT_USERNAME_ROUTE} element={<Outlet />}>
          <Route path={EMPTY} element={<ForgotUserName />} />
          <Route
            path={FORGOT_USERNAME_EMAIL_SENT}
            element={
              isUsernameRetrieved ? (
                <UsernameSent />
              ) : (
                <Navigate to={`/${FORGOT_USERNAME_ROUTE}`} />
              )
            }
          />
        </Route>
        {/**My Profile Settings -Routing **/}
        <Route
          path={MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}
          element={
            isEmpty(jwtTokenFromSession) ? (
              <Navigate to={`/${LOGIN_ROUTE}`} />
            ) : (
              <AuthorizationWrapper />
            )
          }
        >
          <Route index element={<PersonalDetails />} />
          <Route
            path={LOGIN_INFORMATION_ROUTE}
            element={<LoginInformation />}
          />
          <Route path={SITE_ACCESS_ROUTE} element={<SiteAccess />} />
          <Route
            path={SECURITY_PREFERENCES_ROUTE}
            element={<LoginVerificationMethods isProfileRoute={true} />}
          />
          <Route
            path={MANAGE_VERIFICATION_METHOD}
            element={<AddEditVerificationMethod />}
          />
          <Route path={VERIFY_ACCOUNT_ROUTE} element={<VerifyAccount />} />
          <Route path={RESET_PASSWORD_ROUTE} element={<ResetPassword />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
